/**
 * « Performances by platform » — une courbe par plateforme, jour par jour.
 *
 * Suit la période de la page et le sélecteur de plateformes. Une seule mesure à
 * la fois, choisie dans la liste (les colonnes du tableau au-dessus) : Cost et
 * CTR n'ont pas d'échelle commune, et deux axes verticaux feraient lire des
 * croisements qui ne dépendent que des échelles choisies.
 *
 * Couleur attachée à la PLATEFORME, jamais à son rang : Meta reste bleu, Amazon
 * Ads ambre, Google Ads vert, qu'on décoche les autres ou non.
 *
 * Les taux (CTR, CVR, ROAS, CPC, CPM, CPA) sont ceux du jour ; un jour sans
 * dénominateur (aucun clic, aucune dépense) laisse un trou plutôt qu'un zéro.
 *
 * `withBlendedCpa` (Résumé) : sur les mesures Cost et CPA (deux montants en
 * euros, même axe), une courbe « CPA global » en plus — pub de toutes les plateformes ÷ Piano LED payants vendus ce jour-là
 * (Shopify + Amazon), la formule de la tuile CPA. Un jour sans vente : trou.
 */

import type { AdsDayFigures } from '@shared/types/sales';
import { adsRoas } from '@shared/types/sales';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useAuth } from '../hooks/useAuth';
import { ApiRequestError, fetchAdsDailyKpis } from '../lib/api';
import { daysBetween } from '../lib/dates';
import { formatCompact, formatDayLong, formatDayShort, formatDecimal, formatInteger, formatMoney, formatMoneyCompact, formatPercent } from '../lib/format';
import { AD_PLATFORM_COLORS, AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { useChartMarkers } from '../hooks/useChartMarkers';
import { MarkerStrip } from './MarkerStrip';

type Platform = 'meta' | 'amazon' | 'google';
type SeriesKey = Platform | 'global';
type Series = { key: SeriesKey; label: string; color: string; dashed?: boolean };

const PLATFORMS: ReadonlyArray<{ key: Platform; label: string; color: string }> = [
  { key: 'meta', label: 'Meta', color: AD_PLATFORM_COLORS.meta },
  { key: 'amazon', label: 'Amazon Ads', color: AD_PLATFORM_COLORS.amazon },
  { key: 'google', label: 'Google Ads', color: AD_PLATFORM_COLORS.google },
];

const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ratio = (numerator: number, denominator: number) => (denominator > 0 ? numerator / denominator : null);

interface Metric {
  label: string;
  value: (day: AdsDayFigures) => number | null;
  format: (value: number, currency: string) => string;
  axis: (value: number, currency: string) => string;
}

const METRICS = {
  cost: { label: 'Cost (HT)', value: (d) => d.cost, format: (v, c) => formatMoney(v, c), axis: (v, c) => formatMoneyCompact(v, c) },
  impressions: { label: 'Impressions', value: (d) => d.impressions, format: (v) => formatInteger(v), axis: (v) => formatCompact(v) },
  clicks: { label: 'Clicks', value: (d) => d.clicks, format: (v) => formatInteger(v), axis: (v) => formatCompact(v) },
  ctr: {
    label: 'CTR %',
    value: (d) => (d.impressions > 0 ? (d.clicks / d.impressions) * 100 : null),
    format: (v) => formatPercent(v),
    axis: (v) => formatPercent(v),
  },
  conversions: { label: 'Conversions', value: (d) => d.conversions, format: (v) => formatDecimal(v), axis: (v) => formatCompact(v) },
  cvr: {
    label: 'CVR %',
    value: (d) => (d.clicks > 0 ? (d.conversions / d.clicks) * 100 : null),
    format: (v) => formatPercent(v),
    axis: (v) => formatPercent(v),
  },
  conversionValue: {
    label: 'Conversion Value (TTC)',
    value: (d) => d.conversionValue,
    format: (v, c) => formatMoney(v, c),
    axis: (v, c) => formatMoneyCompact(v, c),
  },
  roas: {
    label: 'ROAS (HT)',
    value: (d) => adsRoas(d.conversionValue, d.cost),
    format: (v) => `×${ratioFormatter.format(v)}`,
    axis: (v) => `×${ratioFormatter.format(v)}`,
  },
  cpc: { label: 'CPC (HT)', value: (d) => ratio(d.cost, d.clicks), format: (v, c) => formatMoney(v, c, true), axis: (v, c) => formatMoney(v, c, true) },
  cpm: {
    label: 'CPM (HT)',
    value: (d) => (d.impressions > 0 ? (d.cost / d.impressions) * 1000 : null),
    format: (v, c) => formatMoney(v, c, true),
    axis: (v, c) => formatMoney(v, c, true),
  },
  cpa: { label: 'CPA (HT)', value: (d) => ratio(d.cost, d.conversions), format: (v, c) => formatMoney(v, c, true), axis: (v, c) => formatMoney(v, c, true) },
} satisfies Record<string, Metric>;

type MetricKey = keyof typeof METRICS;

type ChartRow = { date: string } & Partial<Record<SeriesKey, number | null>>;

const GLOBAL_CPA: Series = { key: 'global', label: 'CPA global', color: COLORS.textPrimary, dashed: true };

interface AdsPlatformChartProps {
  /** Période couverte par les données (bornée à aujourd'hui). */
  period: { from: string; to: string };
  withMeta: boolean;
  withAmazon: boolean;
  /** Absent : Google Ads affiché dès qu'il est branché. */
  withGoogle?: boolean;
  currency: string;
  /** Titre de la carte ; par défaut « <mesure> par plateforme ». */
  title?: string;
  /** Nom affiché pour Meta (« Facebook » sur le Résumé). */
  metaLabel?: string;
  /** Sur les mesures Cost et CPA, ajoute la courbe « CPA global » (pub ÷ Piano LED payants vendus). */
  withBlendedCpa?: boolean;
}

function PlatformTooltip({
  active,
  payload,
  metric,
  currency,
  shown,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  metric: Metric;
  currency: string;
  shown: ReadonlyArray<Series>;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{formatDayLong(row.date)}</p>
      {shown.map((platform) => {
        const value = row[platform.key];
        return (
          <div className="tooltip__row" key={platform.key}>
            <span className="legend__key" style={{ background: platform.color }} aria-hidden="true" />
            <span className="tooltip__name">{platform.label}</span>
            <span className="tooltip__value">{value != null ? metric.format(value, currency) : '—'}</span>
          </div>
        );
      })}
    </div>
  );
}


/** Marqueurs « ventes » : impact lu sur la somme des plateformes affichées. */
const MARKER_CONFIG = {
  value: (row: ChartRow) => (row.meta ?? 0) + (row.amazon ?? 0) + (row.google ?? 0),
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

export function AdsPlatformChart({
  period,
  withMeta,
  withAmazon,
  withGoogle = true,
  currency,
  title,
  metaLabel,
  withBlendedCpa = false,
}: AdsPlatformChartProps) {
  const { token, signOut } = useAuth();
  const [metricKey, setMetricKey] = useState<MetricKey>('cost');
  const metric: Metric = METRICS[metricKey];
  const days = Math.max(1, daysBetween(period.from, period.to));

  const query = useQuery({
    queryKey: ['ads-daily', period.to, days],
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return await fetchAdsDailyKpis(period.to, token, signal, days);
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });

  const amazonLive = query.data?.days.some((day) => day.amazon !== null) ?? false;
  // `?? null` : une réponse d'avant Google Ads (gardée en cache) n'a pas le champ.
  const googleLive = query.data?.days.some((day) => (day.google ?? null) !== null) ?? false;
  const visible: Record<Platform, boolean> = { meta: withMeta, amazon: withAmazon && amazonLive, google: withGoogle && googleLive };
  const platforms = PLATFORMS.filter((platform) => visible[platform.key]).map((platform) =>
    platform.key === 'meta' && metaLabel ? { ...platform, label: metaLabel } : platform,
  );
  const withGlobal = withBlendedCpa && (metricKey === 'cost' || metricKey === 'cpa');
  const shown: ReadonlyArray<Series> = withGlobal ? [...platforms, GLOBAL_CPA] : platforms;

  // L'API rend les jours du plus récent au plus ancien.
  const rows: ChartRow[] = [...(query.data?.days ?? [])].reverse().map((day) => {
    const row: ChartRow = { date: day.date };
    for (const platform of platforms) {
      const figures = day[platform.key] ?? null;
      row[platform.key] = figures ? metric.value(figures) : null;
    }
    if (withGlobal) {
      // Toute la pub du jour, comme la tuile CPA — quelles que soient les régies cochées.
      const spend = (day.meta?.cost ?? 0) + (day.amazon?.cost ?? 0) + (day.google?.cost ?? 0);
      row.global = ratio(spend, day.paidKits ?? 0);
    }
    return row;
  });

  const markers = useChartMarkers(rows, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<ChartRow>> = [
    { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
    ...shown.map((platform) => ({
      key: platform.key,
      header: platform.label,
      numeric: true,
      render: (row: ChartRow) => {
        const value = row[platform.key];
        return value != null ? metric.format(value, currency) : '—';
      },
    })),
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const hasData = shown.length > 0 && rows.some((row) => shown.some((platform) => (row[platform.key] ?? 0) !== 0));
  // Au-delà d'un mois, les points se touchent : on garde la ligne seule.
  const dot = rows.length <= 62 ? { r: 4, strokeWidth: 2, stroke: COLORS.surface } : false;

  return (
      <ChartCard
        footer={<MarkerStrip items={markers.items} />}
        className="chart-grid__wide"
        title={title ?? `${metric.label} par plateforme`}
        subtitle={
          withGlobal
            ? 'Chaque jour de la période, chiffres attribués par chaque plateforme — CPA global (pointillés) : pub toutes plateformes ÷ Piano LED payants vendus (Shopify + Amazon)'
            : 'Chaque jour de la période, chiffres attribués par chaque plateforme'
        }
        rows={hasData ? rows : []}
        columns={columns}
        emptyMessage={query.isError ? 'Chiffres quotidiens indisponibles.' : 'Aucune donnée publicitaire sur cette période.'}
        chartHeight={380}
      >
        <div className="platform-chart__bar">
          <div className="field platform-chart__metric">
            <label className="field__label" htmlFor="platform-metric">
              Mesure
            </label>
            <select id="platform-metric" value={metricKey} onChange={(event) => setMetricKey(event.target.value as MetricKey)}>
              {(Object.keys(METRICS) as MetricKey[]).map((key) => (
                <option key={key} value={key}>
                  {METRICS[key].label}
                </option>
              ))}
            </select>
          </div>
          <ChartLegend entries={shown.map((platform) => ({ label: platform.label, color: platform.color }))} />
        </div>
        <div style={{ height: 'calc(100% - 64px)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
              <YAxis
                tickFormatter={(value: number) => metric.axis(value, currency)}
                tick={axisTick}
                axisLine={false}
                tickLine={false}
                width={64}
                domain={[0, 'auto']}
              />
              <Tooltip
                cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
                content={<PlatformTooltip metric={metric} currency={currency} shown={shown} />}
              />
              {markers.lines}
              {shown.map((platform) => (
                <Line
                  key={platform.key}
                  type="monotone"
                  dataKey={platform.key}
                  name={platform.label}
                  stroke={platform.color}
                  strokeWidth={LINE_WIDTH}
                  strokeDasharray={platform.dashed ? '6 4' : undefined}
                  dot={dot ? { ...dot, fill: platform.color } : false}
                  activeDot={{ r: 5, fill: platform.color, stroke: COLORS.surface, strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
  );
}
